// WhyChooseUs.jsx
import React, { useState, useEffect } from "react";
import "./WhyChooseUs.css";

const WhyChooseUs = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const reasons = [
    {
      icon: "🎨",
      title: "Creative Designs",
      description: "We create unique and beautiful decorations that match your style and vision perfectly."
    },
    {
      icon: "⏰",
      title: "On-Time Delivery",
      description: "We value your time and always complete decorations before the event starts."
    },
    {
      icon: "💰",
      title: "Best Price Guarantee",
      description: "Get premium decoration services at affordable prices without any compromise."
    },
    {
      icon: "👨‍👩‍👧‍👦",
      title: "Customer Focus",
      description: "Your happiness is our priority. We listen to your needs and deliver exactly what you want."
    },
    {
      icon: "🌟",
      title: "Quality Materials",
      description: "We use only high-quality materials and flowers to make your event look stunning."
    },
    {
      icon: "🔄",
      title: "Flexible Packages",
      description: "Choose from customized packages that fit your budget and requirements."
    }
  ];

  const testimonials = [
    {
      id: 1,
      name: "Priya Sharma",
      location: "Gurugram, Haryana",
      rating: 5,
      text: "Atoz Events made my wedding day absolutely magical! The decorations were beyond my expectations. Everyone loved the beautiful setup. Highly recommended!",
      image: "https://images.pexels.com/photos/415829/pexels-photo-415829.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop",
      event: "Wedding Decoration"
    },
    {
      id: 2,
      name: "Rahul Verma",
      location: "Delhi",
      rating: 5,
      text: "Amazing work! They did our corporate event decoration and it was professional and stunning. The team was very punctual and cooperative.",
      image: "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop",
      event: "Corporate Event"
    },
    {
      id: 3,
      name: "Neha Gupta",
      location: "Gurugram, Haryana",
      rating: 5,
      text: "My daughter's birthday party was a huge success thanks to Atoz Events. The balloon decorations and themed setup were perfect. The kids loved it!",
      image: "https://images.pexels.com/photos/2380794/pexels-photo-2380794.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop",
      event: "Birthday Party"
    },
    {
      id: 4,
      name: "Amit Singh",
      location: "Delhi",
      rating: 5,
      text: "Professional, creative, and affordable! They did an amazing job with our anniversary decoration. The flowers and lighting created a beautiful romantic atmosphere.",
      image: "https://images.pexels.com/photos/2379005/pexels-photo-2379005.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop",
      event: "Anniversary"
    }
  ];

  // Auto-slide testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const goToTestimonial = (index) => {
    setActiveTestimonial(index);
  };

  const renderStars = (rating) => {
    return "⭐".repeat(rating);
  };

  return (
    <section className="why-choose-us" id="why-choose-us">
      <div className="why-container">
        {/* Section Header */}
        <div className="why-header">
          <span className="why-tag">Why Choose Us</span>
          <h2 className="why-title">
            What Makes Us <span>Different</span>
          </h2>
          <p className="why-subtitle">
            We go above and beyond to make your events special. Here's why 
            hundreds of customers trust us with their celebrations.
          </p>
        </div>

        {/* Reasons Grid */}
        <div className="reasons-grid">
          {reasons.map((reason, index) => (
            <div className="reason-card" key={index}>
              <div className="reason-icon">{reason.icon}</div>
              <h3 className="reason-title">{reason.title}</h3>
              <p className="reason-description">{reason.description}</p>
            </div>
          ))}
        </div>

        {/* Testimonials Section */}
        <div className="testimonials-section">
          <div className="testimonials-header">
            <span className="testimonials-tag">Testimonials</span>
            <h3 className="testimonials-title">
              What Our <span>Clients Say</span>
            </h3>
          </div>

          <div className="testimonials-container">
            <div className="testimonials-wrapper">
              {testimonials.map((testimonial, index) => (
                <div
                  key={testimonial.id}
                  className={`testimonial-card ${index === activeTestimonial ? 'active' : ''}`}
                >
                  <div className="testimonial-content">
                    <div className="testimonial-header">
                      <div className="testimonial-image">
                        <img src={testimonial.image} alt={testimonial.name} loading="lazy" />
                      </div>
                      <div className="testimonial-info">
                        <h4 className="testimonial-name">{testimonial.name}</h4>
                        <span className="testimonial-location">{testimonial.location}</span>
                        <div className="testimonial-rating">
                          {renderStars(testimonial.rating)}
                        </div>
                      </div>
                    </div>
                    <div className="testimonial-event">
                      <span className="event-badge">{testimonial.event}</span>
                    </div>
                    <p className="testimonial-text">"{testimonial.text}"</p>
                    <div className="testimonial-quote-icon">❝</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Testimonial Dots */}
            <div className="testimonial-dots">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  className={`dot ${index === activeTestimonial ? 'active' : ''}`}
                  onClick={() => goToTestimonial(index)}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="trust-badges">
          <div className="trust-badge">
            <span className="trust-number">500+</span>
            <span className="trust-label">Happy Clients</span>
          </div>
          <div className="trust-divider"></div>
          <div className="trust-badge">
            <span className="trust-number">10+</span>
            <span className="trust-label">Years Experience</span>
          </div>
          <div className="trust-divider"></div>
          <div className="trust-badge">
            <span className="trust-number">100%</span>
            <span className="trust-label">Satisfaction</span>
          </div>
          <div className="trust-divider"></div>
          <div className="trust-badge">
            <span className="trust-number">24/7</span>
            <span className="trust-label">Support</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;